import { consoleRoutes, WORKFLOW_LIMITS, WORKFLOW_VARIABLES } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Field, Select, Segmented, Textarea } from "@sagot/ui";
import { useWorkspaceContext } from "../../../../session/workspace-context";
import type { SendWebhookStep } from "../types";
import { useRefs } from "./refs";

const F = "engage.workflows.fields";
/** Shown as code (not a translation): an example of a custom body with variables in strings. */
export const TEMPLATE_EXAMPLE = '{\n  "parent": "{{visitor.name}}",\n  "service": "{{booking.service}}",\n  "when": "{{booking.start_local}}"\n}';

export interface FormProps<S> {
  step: S;
  disabled: boolean;
  onChange: (step: S) => void;
}

export function VariableList() {
  const { t } = useTranslation();
  return (
    <p className="mt-1.5 text-xs text-fg-subtle">
      {t(`${F}.variables`)}:{" "}
      {WORKFLOW_VARIABLES.map((name, i) => (
        <span key={name}>
          {i > 0 ? ", " : ""}
          <code className="font-mono">{`{{${name}}}`}</code>
        </span>
      ))}
    </p>
  );
}

/** Send webhook: a registered endpoint, the standard event or a custom JSON body, a chat line. */
export function WebhookForm({ step, disabled, onChange }: FormProps<SendWebhookStep>) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const { endpoints } = useRefs();
  return (
    <div className="space-y-4">
      <Field label={t(`${F}.endpoint`)} hint={t(`${F}.endpointHint`)}>
        <Select value={step.endpoint_id} disabled={disabled} onChange={(e) => { onChange({ ...step, endpoint_id: e.target.value }); }}>
          <option value="">{t(`${F}.choose`)}</option>
          {endpoints.map((endpoint) => <option key={endpoint.id} value={endpoint.id}>{endpoint.name}</option>)}
        </Select>
      </Field>
      {endpoints.length === 0 ? (
        <p className="text-xs text-fg-muted">
          {t(`${F}.noEndpoints`)}{" "}
          <AppLink className="font-semibold text-primary-ink underline" href={consoleRoutes.settings(summary.slug, "webhooks")}>{t(`${F}.openWebhooks`)}</AppLink>
        </p>
      ) : null}
      <div>
        <span className="label">{t(`${F}.body`)}</span>
        <Segmented
          aria-label={t(`${F}.body`)}
          value={step.body}
          onValueChange={(body) => { onChange({ ...step, body }); }}
          options={(["standard", "custom"] as const).map((value) => ({ value, label: t(`${F}.bodies.${value}`) }))}
        />
      </div>
      {step.body === "custom" ? (
        <Field label={t(`${F}.template`)} hint={t(`${F}.templateHint`)}>
          <Textarea rows={8} spellCheck={false} className="font-mono text-xs" maxLength={WORKFLOW_LIMITS.webhookTemplate} placeholder={TEMPLATE_EXAMPLE} disabled={disabled} value={step.template ?? ""} onChange={(e) => { onChange({ ...step, template: e.target.value }); }} />
        </Field>
      ) : null}
      <Field label={t(`${F}.message`)} hint={t(`${F}.messageHint`)}>
        <Textarea rows={2} maxLength={WORKFLOW_LIMITS.notifyBody} disabled={disabled} value={step.message ?? ""} onChange={(e) => { onChange({ ...step, message: e.target.value || undefined }); }} />
      </Field>
      <VariableList />
    </div>
  );
}
